import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { UrlParameters } from './model/url-parameters.model';
import { ExecutingTaskMonitorService } from './executing-task-monitor.service';
import { ExecutingTask } from '../shared/spinner-full-page/executing-task.model';
import { ImageType } from '../shared/spinner-full-page/model/image-type.enum';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly _url: string;
  constructor(
    private readonly httpClient: HttpClient,
    private readonly _executingTaskSvc: ExecutingTaskMonitorService
  ) {
    this._url = environment.api_url;
  }
  public getAll<T>(
    endpoint: string,
    task: string,
    showParitialPageSpinner: boolean
  ): Observable<T[]> {
    let id = crypto.randomUUID();
    let headers = new HttpHeaders()
      .set('x-cdi-task', task ?? '')
      .set('x-cdi-id', id)
      .set('x-cdi-showPartialPageSpinner', showParitialPageSpinner.toString());
    this._executingTaskSvc.executingTasks.push(
      new ExecutingTask(id, task, ImageType.Spinner, new Date().getTime())
    );
    return this.httpClient.get<T[]>(`${this._url}/${endpoint}`, {
      headers: headers,
    });
  }

  public getAllWithQueryParameters<T>(
    endpoint: string,
    queryParameters: UrlParameters[],
    task: string,
    showParitialPageSpinner: boolean
  ): Observable<T[]> {
    let urlParameters = new HttpParams();
    queryParameters.forEach((parm) => {
      urlParameters = urlParameters.append(parm.key, parm.value);
    });
    let id = crypto.randomUUID();
    let headers = new HttpHeaders()
      .set('x-cdi-task', task ?? '')
      .set('x-cdi-id', id)
      .set('x-cdi-showPartialPageSpinner', showParitialPageSpinner.toString());
    this._executingTaskSvc.executingTasks.push(
      new ExecutingTask(id, task, ImageType.Spinner, new Date().getTime())
    );
    return this.httpClient.get<T[]>(`${this._url}/${endpoint}`, {
      params: urlParameters,
      headers: headers,
    });
  }

  public get<T>(
    endpoint: string,
    task: string,
    showParitialPageSpinner: boolean
  ): Observable<T> {
    let id = crypto.randomUUID();
    let headers = new HttpHeaders()
      .set('x-cdi-task', task ?? '')
      .set('x-cdi-id', id)
      .set('x-cdi-showPartialPageSpinner', showParitialPageSpinner.toString());
    this._executingTaskSvc.executingTasks.push(
      new ExecutingTask(id, task, ImageType.Spinner, new Date().getTime())
    );
    return this.httpClient.get<T>(`${this._url}/${endpoint}`, {
      headers: headers,
    });
  }

  public add<T>(
    endpoint: string,
    item: T,
    task: string,
    showParitialPageSpinner: boolean
  ): Observable<any> {
    let id = crypto.randomUUID();
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('x-cdi-task', task ?? '')
      .set('x-cdi-id', id)
      .set('x-cdi-showPartialPageSpinner', showParitialPageSpinner.toString());
    this._executingTaskSvc.executingTasks.push(
      new ExecutingTask(id, task, ImageType.Spinner, new Date().getTime())
    );
    return this.httpClient.post<T>(`${this._url}/${endpoint}`, item, {
      headers: headers,
    });
  }

  public upload<T>(
    endpoint: string,
    item: T,
    task: string,
    showParitialPageSpinner: boolean
  ): Observable<any> {
    let id = crypto.randomUUID();
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/octet-stream')
      .set('x-cdi-task', task ?? '')
      .set('x-cdi-id', id)
      .set('x-cdi-showPartialPageSpinner', showParitialPageSpinner.toString());
    this._executingTaskSvc.executingTasks.push(
      new ExecutingTask(id, task, ImageType.Spinner, new Date().getTime())
    );
    return this.httpClient.post<T>(`${this._url}/${endpoint}`, item, {
      headers: headers,
    });
  }

  public update<T>(
    endpoint: string,
    item: T,
    task: string,
    showParitialPageSpinner: boolean
  ): Observable<any> {
    let id = crypto.randomUUID();
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('x-cdi-task', task ?? '')
      .set('x-cdi-id', id)
      .set('x-cdi-showPartialPageSpinner', showParitialPageSpinner.toString());
    this._executingTaskSvc.executingTasks.push(
      new ExecutingTask(id, task, ImageType.Spinner, new Date().getTime())
    );
    return this.httpClient.put<T>(`${this._url}/${endpoint}`, item, {
      headers: headers,
    });
  }

  public delete(
    endpoint: string,
    task: string,
    showParitialPageSpinner: boolean
  ): Observable<any> {
    let id = crypto.randomUUID();
    let headers = new HttpHeaders()
      .set('x-cdi-task', task ?? '')
      .set('x-cdi-id', id)
      .set('x-cdi-showPartialPageSpinner', showParitialPageSpinner.toString());
    this._executingTaskSvc.executingTasks.push(
      new ExecutingTask(id, task, ImageType.Spinner, new Date().getTime())
    );
    return this.httpClient.delete(`${this._url}/${endpoint}`, {
      headers: headers,
    });
  }

  public download(endpoint: string): Observable<Blob> {
    return this.httpClient.get(`${this._url}/${endpoint}`, {
      responseType: 'blob',
    });
  }

  public getCurrentDateInISO8601Format(): string {
    // yyyy-mm-dd
    const dt = new Date();
    const year = dt.getFullYear();
    const month = (dt.getMonth() + 1).toString().padStart(2, '0');
    const day = dt.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  public getISO8601Date(date: Date): string {
    // yyyy-mm-dd
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
